import { useEffect, useState, useCallback } from "react";
import api, { formatUSD, resolveImage } from "@/lib/api";
import { AlertTriangle, Search, Download, Plus, Minus, History, Save, Filter } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const UNIVERSES = [
  { value: "street_market", label: "Dakar Street Market" },
];

const SORT_OPTIONS = [
  { value: "stock_asc", label: "Stock low → high" },
  { value: "stock_desc", label: "Stock high → low" },
  { value: "name_asc", label: "Name A → Z" },
  { value: "universe", label: "By universe" },
];

export default function AdminStock() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [universe, setUniverse] = useState("street_market");
  const [sort, setSort] = useState("stock_asc");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDelta, setBulkDelta] = useState(10);
  const [bulkReason, setBulkReason] = useState("Restock");
  const [movements, setMovements] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [editing, setEditing] = useState({}); // {id: {stock, low_stock_threshold}}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (q) params.q = q;
      params.universe = "street_market"; // Stock only tracks Street Market products
      const res = await api.get("/admin/stock", { params });
      setProducts(res.data);
    } catch { toast.error("Failed to load stock"); }
    finally { setLoading(false); }
  }, [q, universe, sort]);

  useEffect(() => { load(); }, [load]);

  const setLocal = (id, field, value) => {
    setEditing((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const saveRow = async (p) => {
    const ed = editing[p.id] || {};
    const payload = {};
    if (ed.stock !== undefined && Number(ed.stock) !== p.stock) payload.stock = Number(ed.stock);
    if (ed.low_stock_threshold !== undefined && Number(ed.low_stock_threshold) !== p.low_stock_threshold) payload.low_stock_threshold = Number(ed.low_stock_threshold);
    if (Object.keys(payload).length === 0) return toast.info("No change");
    payload.reason = ed.reason || "manual_set";
    try {
      await api.patch(`/admin/stock/${p.id}`, payload);
      toast.success("Stock saved");
      setEditing((prev) => { const cp = { ...prev }; delete cp[p.id]; return cp; });
      load();
    } catch { toast.error("Save failed"); }
  };

  const adjust = async (p, delta) => {
    const newStock = Math.max(0, p.stock + delta);
    try {
      await api.patch(`/admin/stock/${p.id}`, { stock: newStock, reason: delta > 0 ? "increment" : "decrement" });
      load();
    } catch { toast.error("Adjust failed"); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(products.map((p) => p.id)));
  };

  const bulkApply = async () => {
    if (selectedIds.size === 0) return toast.error("Select products first");
    if (!bulkDelta || Number(bulkDelta) === 0) return toast.error("Delta cannot be zero");
    try {
      await api.post("/admin/stock/bulk", { product_ids: [...selectedIds], delta: Number(bulkDelta), reason: bulkReason });
      toast.success(`Applied ${bulkDelta > 0 ? "+" : ""}${bulkDelta} to ${selectedIds.size} products`);
      setSelectedIds(new Set());
      load();
    } catch { toast.error("Bulk failed"); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("dsf_admin_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/stock/export.csv`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `stock-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const openHistory = async (product) => {
    setHistoryProduct(product);
    try {
      const res = await api.get("/admin/stock/movements", { params: { product_id: product?.id, limit: 50 } });
      setMovements(res.data);
      setHistoryOpen(true);
    } catch { toast.error("History failed"); }
  };

  const lowStock = products.filter((p) => p.stock < (p.low_stock_threshold || 20));
  const outOfStock = products.filter((p) => p.stock === 0);

  return (
    <div className="space-y-6" data-testid="admin-stock-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-bold text-3xl text-[var(--dd-green)]">Stock</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">Manage inventory, thresholds & movements</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => openHistory(null)} className="btn-pill-outline !py-2.5" data-testid="stock-all-history"><History size={14} /> All movements</button>
          <button onClick={exportCsv} className="btn-mustard !py-2.5" data-testid="stock-export-csv"><Download size={14} /> Export CSV</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-black/5 p-5">
          <div className="text-xs uppercase tracking-wider text-[var(--dsf-text-muted)] mb-2">Total products</div>
          <div className="font-bold text-3xl text-[var(--dd-green)]">{products.length}</div>
        </div>
        <div className="bg-white rounded-2xl border border-orange-200 p-5">
          <div className="text-xs uppercase tracking-wider text-[var(--dd-mustard-dark)] mb-2 flex items-center gap-1.5"><AlertTriangle size={12} /> Low stock</div>
          <div className="font-bold text-3xl text-[var(--dd-mustard-dark)]">{lowStock.length}</div>
        </div>
        <div className="bg-white rounded-2xl border border-red-200 p-5">
          <div className="text-xs uppercase tracking-wider text-[var(--dd-coral)] mb-2">Out of stock</div>
          <div className="font-bold text-3xl text-[var(--dd-coral)]">{outOfStock.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-black/5 p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="stock-filters">
        <div className="md:col-span-2 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--dsf-text-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product…" className="pl-10" data-testid="stock-search" />
        </div>
        <select value={universe} onChange={(e) => setUniverse(e.target.value)} className="border border-black/10 rounded-md px-3 text-sm" data-testid="stock-universe-filter">
          {UNIVERSES.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-black/10 rounded-md px-3 text-sm" data-testid="stock-sort">
          {SORT_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="bg-[var(--dd-green)] text-white rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="stock-bulk-bar">
          <div className="text-sm font-semibold">{selectedIds.size} selected</div>
          <div className="flex items-center gap-2">
            <span className="text-xs">Delta:</span>
            <input type="number" value={bulkDelta} onChange={(e) => setBulkDelta(e.target.value)} className="w-20 bg-white/10 border border-white/20 rounded px-2 py-1.5 text-sm" data-testid="stock-bulk-delta" />
            <input type="text" value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} placeholder="Reason" className="w-40 bg-white/10 border border-white/20 rounded px-2 py-1.5 text-sm" data-testid="stock-bulk-reason" />
          </div>
          <button onClick={bulkApply} className="btn-mustard !py-2" data-testid="stock-bulk-apply">Apply to {selectedIds.size}</button>
          <button onClick={() => setSelectedIds(new Set())} className="text-white/80 text-xs hover:text-white">Clear</button>
        </div>
      )}

      {/* Stock table */}
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stock-table">
            <thead className="bg-[var(--dd-bone)]">
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[var(--dd-green)] font-bold">
                <th className="px-3 py-3 w-8"><input type="checkbox" checked={selectedIds.size === products.length && products.length > 0} onChange={toggleSelectAll} className="accent-[var(--dd-green)]" data-testid="stock-select-all" /></th>
                <th className="px-3 py-3">Product</th>
                <th className="px-3 py-3">Universe</th>
                <th className="px-3 py-3">Price</th>
                <th className="px-3 py-3">Stock</th>
                <th className="px-3 py-3">Threshold</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="text-center py-12 text-[var(--dsf-text-muted)]">Loading…</td></tr>}
              {!loading && products.length === 0 && (
                <tr><td colSpan="8" className="text-center py-12 text-[var(--dsf-text-muted)]">No products</td></tr>
              )}
              {products.map((p) => {
                const ed = editing[p.id] || {};
                const stockVal = ed.stock !== undefined ? ed.stock : p.stock;
                const thresholdVal = ed.low_stock_threshold !== undefined ? ed.low_stock_threshold : (p.low_stock_threshold ?? 20);
                const status = p.stock === 0 ? "OUT" : (p.stock < (p.low_stock_threshold || 20) ? "LOW" : "OK");
                const statusCls = status === "OUT" ? "bg-red-100 text-red-700" : status === "LOW" ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700";
                const dirty = ed.stock !== undefined || ed.low_stock_threshold !== undefined;
                const pct = Math.min(100, (p.stock / Math.max(1, (p.low_stock_threshold || 20) * 3)) * 100);
                const barColor = status === "OUT" ? "bg-red-500" : status === "LOW" ? "bg-orange-500" : "bg-emerald-500";

                return (
                  <tr key={p.id} className="border-t border-black/5 hover:bg-[var(--dd-bone)]/40" data-testid={`stock-row-${p.slug}`}>
                    <td className="px-3 py-3"><input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} className="accent-[var(--dd-green)]" data-testid={`stock-select-${p.slug}`} /></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-3">
                        <img src={resolveImage(p.image)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        <div>
                          <div className="font-medium text-[var(--dsf-text)]">{p.name}</div>
                          <div className="text-xs text-[var(--dsf-text-muted)]">{p.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-[var(--dsf-text-secondary)] capitalize">{p.universe?.replace("_", " ")}</td>
                    <td className="px-3 py-3 font-semibold text-[var(--dd-green)]">{formatUSD(p.price)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => adjust(p, -1)} className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center hover:bg-[var(--dd-bone)]" data-testid={`stock-dec-${p.slug}`}><Minus size={12} /></button>
                        <input
                          type="number"
                          value={stockVal}
                          onChange={(e) => setLocal(p.id, "stock", e.target.value)}
                          className="w-20 border border-black/10 rounded text-center py-1 text-sm font-semibold"
                          data-testid={`stock-input-${p.slug}`}
                        />
                        <button onClick={() => adjust(p, +1)} className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center hover:bg-[var(--dd-bone)]" data-testid={`stock-inc-${p.slug}`}><Plus size={12} /></button>
                      </div>
                      <div className="w-full bg-[var(--dd-bone)] rounded-full h-1 mt-2">
                        <div className={`h-1 rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        value={thresholdVal}
                        onChange={(e) => setLocal(p.id, "low_stock_threshold", e.target.value)}
                        className="w-16 border border-black/10 rounded text-center py-1 text-sm"
                        data-testid={`stock-threshold-${p.slug}`}
                      />
                    </td>
                    <td className="px-3 py-3"><span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${statusCls}`}>{status}</span></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {dirty && (
                          <button onClick={() => saveRow(p)} className="text-[var(--dd-green)] hover:text-[var(--dd-mustard)]" title="Save" data-testid={`stock-save-${p.slug}`}><Save size={15} /></button>
                        )}
                        <button onClick={() => openHistory(p)} className="text-[var(--dsf-text-muted)] hover:text-[var(--dd-green)]" title="History" data-testid={`stock-history-${p.slug}`}><History size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{historyProduct ? `History — ${historyProduct.name}` : "All stock movements"}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-1">
            {movements.length === 0 && <div className="text-[var(--dsf-text-muted)] py-8 text-center">No movements yet</div>}
            {movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-2 border-b border-black/5 text-sm" data-testid={`movement-${m.id}`}>
                <div className="flex-1">
                  {!historyProduct && <div className="font-medium text-[var(--dsf-text)] text-xs">{m.product_name}</div>}
                  <div className="text-xs text-[var(--dsf-text-muted)]">{m.created_at?.slice(0, 16).replace("T", " ")} · {m.reason}</div>
                </div>
                <div className={`font-bold ${m.delta > 0 ? "text-[var(--dd-green)]" : "text-[var(--dd-coral)]"}`}>{m.delta > 0 ? "+" : ""}{m.delta}</div>
                <div className="ml-3 text-xs text-[var(--dsf-text-muted)]">→ {m.stock_after}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
